import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ProjectDetail = ({ projects }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Find the project with the matching id
  const project = projects.find(p => p.id.toString() === id);
  
  // If project not found, show error and redirect button
  if (!project) {
    return (
      <div className="pt-24 pb-16">
        <div className="section">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="section-title">Project Not Found</h1>
            <p className="text-gray-400 mb-8">
              The project you're looking for doesn't exist or has been removed.
            </p>
            <button 
              onClick={() => navigate('/projects')}
              className="btn-primary"
            >
              View All Side Projects
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="pt-24 pb-16">
      <div className="section">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <Link 
            to="/projects" 
            className="inline-flex items-center text-gray-400 hover:text-primary-400 mb-8 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Side Projects
          </Link>
          
          {/* Project image */}
          <div className="relative h-80 sm:h-96 md:h-[30rem] mb-8 rounded-lg overflow-hidden shadow-lg">
            <img 
              src={project.image} 
              alt={project.title} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-900 to-transparent opacity-70"></div>
            <div className="absolute bottom-0 left-0 p-6">
              <h1 className="text-4xl font-bold text-white mb-2">{project.title}</h1>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, index) => (
                  <span key={index} className="skill-badge">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Project links */}
          <div className="flex flex-wrap gap-4 mb-8">
            {project.github_url && (
              <a 
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline flex items-center"
              >
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
                View Source Code
              </a>
            )}
            
            {project.live_url && (
              <a 
                href={project.live_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center"
              >
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15.75 2.25H8.25C5.25 2.25 3.75 3.75 3.75 6.75V17.25C3.75 20.25 5.25 21.75 8.25 21.75H15.75C18.75 21.75 20.25 20.25 20.25 17.25V6.75C20.25 3.75 18.75 2.25 15.75 2.25ZM19.5 17.25C19.5 19.8 18.3 21 15.75 21H8.25C5.7 21 4.5 19.8 4.5 17.25V6.75C4.5 4.2 5.7 3 8.25 3H15.75C18.3 3 19.5 4.2 19.5 6.75V17.25Z" />
                  <path d="M12 15.75C14.8995 15.75 17.25 13.3995 17.25 10.5C17.25 7.60051 14.8995 5.25 12 5.25C9.10051 5.25 6.75 7.60051 6.75 10.5C6.75 13.3995 9.10051 15.75 12 15.75Z" />
                </svg>
                View Live Demo
              </a>
            )}
          </div>
          
          {/* Project description */}
          <div className="card p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Project Overview</h2>
            <p className="text-gray-300 leading-relaxed whitespace-pre-line">
              {project.description}
            </p>
          </div>
          
          <div className="card p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Build Notes</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-semibold text-primary-300">Technologies Used</h3>
                <ul className="mt-3 space-y-2 text-gray-300">
                  {project.technologies.map((tech, index) => (
                    <li key={index}>{tech}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-primary-300">Project Type</h3>
                <p className="mt-3 text-gray-300">
                  Side project, prototype, or practical experiment built to explore an idea and learn by shipping.
                </p>
              </div>
            </div>
          </div>
          
          {/* Navigation between projects */}
          <div className="flex justify-between mt-12">
            {projects.find(p => p.id < project.id) ? (
              <Link 
                to={`/projects/${project.id - 1}`}
                className="flex items-center text-gray-400 hover:text-primary-400 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Previous Side Project
              </Link>
            ) : (
              <div></div>
            )}
            
            {projects.find(p => p.id > project.id) ? (
              <Link 
                to={`/projects/${project.id + 1}`}
                className="flex items-center text-gray-400 hover:text-primary-400 transition-colors"
              >
                Next Side Project
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
